import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../context/CurrencyContext';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { 
  Monitor, 
  ChefHat, 
  Truck, 
  BarChart3, 
  Clock, 
  Users, 
  CreditCard, 
  Smartphone,
  Wifi,
  Shield,
  Zap,
  Check,
  ArrowRight,
  Play,
  Star,
  ChevronRight,
  Receipt,
  UtensilsCrossed,
  Calculator,
  Bell,
  Settings,
  Globe
} from 'lucide-react';

export default function RestaurantOSLandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState('pos');
  
  // Get currency from context (controlled by currency selector, not language)
  const { currency, currencyConfig, convertCurrency } = useCurrency();
  const currencySymbol = currencyConfig.symbol;
  
  // Convert GBP price to selected currency
  const getPrice = (gbpPrice) => {
    if (currency === 'EUR') {
      return Math.round(convertCurrency(gbpPrice, 'GBP', 'EUR'));
    }
    return gbpPrice;
  };

  // Module screenshots
  const moduleImages = {
    pos: 'https://static.prod-images.emergentagent.com/jobs/90e3b3b5-1473-45b4-9010-88017152206f/images/2b441f9504ae45fd2d6d8f49ca46380d306b3169d45852c9a54e8fd14d0eaf5f.png',
    kitchen: 'https://static.prod-images.emergentagent.com/jobs/90e3b3b5-1473-45b4-9010-88017152206f/images/0cb62705ceb8d7c452c62cb335f7d123de074945e0d67e2034da9f51c85bc234.png',
    delivery: 'https://static.prod-images.emergentagent.com/jobs/90e3b3b5-1473-45b4-9010-88017152206f/images/3d87ccc263dce05f23baf861c0f793a2fe744c6582cb30d3a07ee0dcf2e5b5f4.png',
    analytics: 'https://static.prod-images.emergentagent.com/jobs/90e3b3b5-1473-45b4-9010-88017152206f/images/2e956076791ac635ae34820683be3f11e12fb38c8a5b383cdb354183144fda6a.png'
  };

  const modules = {
    pos: {
      icon: <Monitor className="w-8 h-8" />,
      title: t('restaurantOS.modules.pos.title', 'Point of Sale'),
      description: t('restaurantOS.modules.pos.description', 'Touch-optimized till system that works on any device. Process orders, handle payments, and manage your floor effortlessly.'),
      features: [
        t('restaurantOS.modules.pos.features.quickOrder', 'Quick order entry'),
        t('restaurantOS.modules.pos.features.payments', 'Cash & card payments'),
        t('restaurantOS.modules.pos.features.billSplit', 'Bill splitting'),
        t('restaurantOS.modules.pos.features.parkOrders', 'Park & recall orders'),
        t('restaurantOS.modules.pos.features.shifts', 'Shift management'),
        t('restaurantOS.modules.pos.features.printing', 'Receipt printing')
      ]
    },
    kitchen: {
      icon: <ChefHat className="w-8 h-8" />,
      title: t('restaurantOS.modules.kitchen.title', 'Kitchen Display'),
      description: t('restaurantOS.modules.kitchen.description', 'Real-time kitchen workflow management. Route orders to stations, track prep times, and never miss a note.'),
      features: [
        t('restaurantOS.modules.kitchen.features.queue', 'Real-time order queue'),
        t('restaurantOS.modules.kitchen.features.routing', 'Station routing'),
        t('restaurantOS.modules.kitchen.features.timers', 'Timer alerts'),
        t('restaurantOS.modules.kitchen.features.allergy', 'Allergy highlighting'),
        t('restaurantOS.modules.kitchen.features.bump', 'Bump to complete'),
        t('restaurantOS.modules.kitchen.features.multiScreen', 'Multi-screen support')
      ]
    },
    delivery: {
      icon: <Truck className="w-8 h-8" />,
      title: t('restaurantOS.modules.delivery.title', 'Delivery Management'),
      description: t('restaurantOS.modules.delivery.description', 'Built-in delivery tracking and driver management. Accept online orders and track every delivery in real-time.'),
      features: [
        t('restaurantOS.modules.delivery.features.online', 'Online ordering'),
        t('restaurantOS.modules.delivery.features.tracking', 'Driver tracking'),
        t('restaurantOS.modules.delivery.features.routes', 'Route optimization'),
        t('restaurantOS.modules.delivery.features.notifications', 'Customer notifications'),
        t('restaurantOS.modules.delivery.features.zones', 'Delivery zones'),
        t('restaurantOS.modules.delivery.features.payouts', 'Driver payouts')
      ]
    },
    analytics: {
      icon: <BarChart3 className="w-8 h-8" />,
      title: t('restaurantOS.modules.analytics.title', 'Analytics & Reports'),
      description: t('restaurantOS.modules.analytics.description', 'Understand your business with powerful insights. Track sales, identify trends, and make data-driven decisions.'),
      features: [
        t('restaurantOS.modules.analytics.features.sales', 'Sales reports'),
        t('restaurantOS.modules.analytics.features.items', 'Item performance'),
        t('restaurantOS.modules.analytics.features.staff', 'Staff analytics'),
        t('restaurantOS.modules.analytics.features.peak', 'Peak hour analysis'),
        t('restaurantOS.modules.analytics.features.revenue', 'Revenue tracking'),
        t('restaurantOS.modules.analytics.features.export', 'Export to CSV')
      ]
    }
  };

  const painPoints = [
    { icon: <Monitor className="w-6 h-6" />, text: t('restaurantOS.painPoints.pos', 'POS System') },
    { icon: <ChefHat className="w-6 h-6" />, text: t('restaurantOS.painPoints.kitchen', 'Kitchen Display') },
    { icon: <Smartphone className="w-6 h-6" />, text: t('restaurantOS.painPoints.online', 'Online Ordering') },
    { icon: <Truck className="w-6 h-6" />, text: t('restaurantOS.painPoints.delivery', 'Delivery Tracking') },
    { icon: <Users className="w-6 h-6" />, text: t('restaurantOS.painPoints.staff', 'Staff Management') },
    { icon: <CreditCard className="w-6 h-6" />, text: t('restaurantOS.painPoints.payments', 'Payments') },
    { icon: <BarChart3 className="w-6 h-6" />, text: t('restaurantOS.painPoints.analytics', 'Analytics') }
  ];

  const benefits = [
    {
      icon: <Wifi className="w-8 h-8 text-orange-500" />,
      title: t('restaurantOS.whyChoose.noHardware.title', 'No Hardware Required'),
      description: t('restaurantOS.whyChoose.noHardware.description', 'Works on any tablet, phone, or computer you already own. No expensive terminals to buy.')
    },
    {
      icon: <Zap className="w-8 h-8 text-orange-500" />,
      title: t('restaurantOS.whyChoose.setup.title', 'Setup in Minutes'),
      description: t('restaurantOS.whyChoose.setup.description', 'No technician visits needed. Sign up, add your menu, and start taking orders today.')
    },
    {
      icon: <Globe className="w-8 h-8 text-orange-500" />,
      title: t('restaurantOS.whyChoose.cloud.title', 'Cloud-Based'),
      description: t('restaurantOS.whyChoose.cloud.description', 'Access your data from anywhere. Updates happen automatically. Always running the latest version.')
    },
    {
      icon: <Shield className="w-8 h-8 text-orange-500" />,
      title: t('restaurantOS.whyChoose.secure.title', 'Secure & Reliable'),
      description: t('restaurantOS.whyChoose.secure.description', 'Bank-level encryption. Daily backups. 99.9% uptime guarantee.')
    }
  ];

  const testimonials = [
    {
      quote: t('restaurantOS.testimonials.quote1', "We replaced 4 different systems with Hambre OS. Everything just works together now."),
      author: "Maria Garcia",
      role: t('restaurantOS.testimonials.role1', "Owner, Tapas Barcelona"),
      rating: 5
    },
    {
      quote: t('restaurantOS.testimonials.quote2', "The kitchen display alone saved us hours every week. No more lost tickets."),
      author: "James Chen",
      role: t('restaurantOS.testimonials.role2', "Head Chef, Golden Dragon"),
      rating: 5
    },
    {
      quote: t('restaurantOS.testimonials.quote3', "Finally a POS that doesn't need a PhD to operate. My staff learned it in an hour."),
      author: "Ahmed Hassan",
      role: t('restaurantOS.testimonials.role3', "Manager, Spice Route"),
      rating: 5
    }
  ];

  const pricing = [
    {
      name: t('pricing.plans.starter.name', 'Starter'),
      price: '49',
      description: t('pricing.plans.starter.description', 'Perfect for small restaurants'),
      features: [
        t('pricing.plans.starter.features.pos', 'POS System'),
        t('pricing.plans.starter.features.menu', 'Menu Management'),
        t('pricing.plans.starter.features.reports', 'Basic Reports'),
        t('pricing.plans.starter.features.location', '1 Location'),
        t('pricing.plans.starter.features.support', 'Email Support')
      ],
      cta: t('pricing.startTrial', 'Start Free Trial'),
      popular: false
    },
    {
      name: t('pricing.plans.professional.name', 'Professional'),
      price: '99',
      description: t('pricing.plans.professional.description', 'Everything you need to grow'),
      features: [
        t('pricing.plans.professional.features.starter', 'Everything in Starter'),
        t('pricing.plans.professional.features.kitchen', 'Kitchen Display'),
        t('pricing.plans.professional.features.online', 'Online Ordering'),
        t('pricing.plans.professional.features.delivery', 'Delivery Management'),
        t('pricing.plans.professional.features.analytics', 'Advanced Analytics'),
        t('pricing.plans.professional.features.support', 'Priority Support')
      ],
      cta: t('pricing.startTrial', 'Start Free Trial'),
      popular: true
    },
    {
      name: t('pricing.plans.enterprise.name', 'Enterprise'),
      price: t('pricing.custom', 'Custom'),
      description: t('pricing.plans.enterprise.description', 'For restaurant chains'),
      features: [
        t('pricing.plans.enterprise.features.professional', 'Everything in Professional'),
        t('pricing.plans.enterprise.features.multiLocation', 'Multi-location'),
        t('pricing.plans.enterprise.features.integrations', 'Custom Integrations'),
        t('pricing.plans.enterprise.features.manager', 'Dedicated Account Manager'),
        t('pricing.plans.enterprise.features.training', 'Custom Training'),
        t('pricing.plans.enterprise.features.sla', 'SLA Guarantee')
      ],
      cta: t('pricing.contactSales', 'Contact Sales'),
      popular: false
    }
  ];

  return (
    <>
      <SEO page="restaurantOS" />
      
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-20 pb-32">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }} />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center max-w-4xl mx-auto">
              {/* Special Offer Banner */}
              <div className="mb-8 inline-block">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-full shadow-lg animate-pulse">
                  <span className="font-bold text-lg">🎉 {t('specialOffer.title', 'SPECIAL OFFER')}: 50% OFF</span>
                  <span className="ml-2">{t('specialOffer.subtitle', 'for 12 months - Sign up before April 1st!')}</span>
                </div>
              </div>

              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                </span>
                <span className="text-orange-300 text-sm font-medium">{t('badge.newFeature', 'New: AI-Powered Menu and Receipt Scanning')}</span>
              </div>
              
              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="hero-headline">
                {t('hero.title1', 'Run Your Entire Restaurant')}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                  {t('hero.title2', 'From One Platform')}
                </span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                {t('hero.subtitle', 'POS, Kitchen Display, Online Ordering, Delivery, Staff Management & Analytics. All connected. No expensive hardware required.')}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  onClick={() => navigate('/register?role=restaurant_admin')}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all"
                  data-testid="cta-start-free"
                >
                  {t('cta.startFreeTrial', 'Start Free Trial')} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/restaurants')}
                  className="border-slate-500 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="cta-see-demo"
                >
                  <Play className="w-5 h-5 mr-2" /> {t('cta.seeLiveDemo', 'See Live Demo')}
                </Button>
              </div>

              {/* Customer CTA - First Instance */}
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl p-6 mb-12 shadow-xl">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="text-left">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <UtensilsCrossed className="w-6 h-6" />
                      {t('forCustomers.title', 'Looking to Order Food?')}
                    </h3>
                    <p className="text-blue-100">
                      {t('forCustomers.description', 'Browse our partner restaurants, order delivery or takeaway, and track your food in real-time.')}
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/restaurants')}
                    className="bg-white text-blue-600 hover:bg-blue-50 rounded-full px-8 py-3 font-semibold whitespace-nowrap"
                    data-testid="browse-restaurants-hero"
                  >
                    {t('forCustomers.cta', 'Browse Restaurants')} <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center gap-8 text-slate-400 text-sm">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{t('pricing.freeTrial', '14-day free trial')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{t('pricing.noCreditCard', 'No credit card required')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{t('pricing.cancelAnytime', 'Cancel anytime')}</span>
                </div>
              </div>
            </div>
            
            {/* Hero Image/Dashboard Preview */}
            <div className="mt-16 relative">
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent z-10 pointer-events-none" />
              <div className="bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className="flex-1 text-center text-slate-500 text-sm">{t('dashboard.title', 'Hambre OS Dashboard')}</div>
                </div>
                <div className="p-6 bg-slate-900/50">
                  <div className="grid grid-cols-4 gap-4">
                    {Object.entries(modules).map(([key, module]) => (
                      <div key={key} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                        <div className="text-orange-500 mb-2">{module.icon}</div>
                        <div className="text-white font-medium text-sm">{module.title}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pain Point Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('restaurantOS.painPoints.title', 'Tired of Juggling 7 Different Systems?')}
              </h2>
              <p className="text-lg text-slate-600">
                {t('restaurantOS.painPoints.subtitle', "Most restaurants waste hours every day switching between disconnected tools. Your POS doesn't talk to your kitchen display. Your online orders live in a separate app. Sound familiar?")}
              </p>
            </div>
            
            {/* Systems Grid */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {painPoints.map((point, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-md border border-slate-200"
                >
                  <div className="text-slate-400">{point.icon}</div>
                  <span className="text-slate-700 font-medium">{point.text}</span>
                </div>
              ))}
            </div>
            
            {/* Arrow Down */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-500 text-white">
                <ArrowRight className="w-6 h-6 rotate-90" />
              </div>
            </div>
            
            {/* Solution */}
            <div className="text-center">
              <div className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white text-lg font-bold px-8 py-4 rounded-full shadow-lg">
                {t('restaurantOS.solution.title', 'One Platform. Everything Connected.')}
              </div>
            </div>
          </div>
        </section>

        {/* Module Showcase Section */}
        <section className="py-20 bg-white" id="features">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('restaurantOS.allFeatures.title', 'Everything You Need to Run Your Restaurant')}
              </h2>
              <p className="text-lg text-slate-600">
                {t('restaurantOS.allFeatures.subtitle', 'From taking orders to analyzing performance - all in one integrated platform.')}
              </p>
            </div>
            
            {/* Module Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {Object.entries(modules).map(([key, module]) => (
                <button
                  key={key}
                  onClick={() => setActiveModule(key)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                    activeModule === key 
                      ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`module-tab-${key}`}
                >
                  {module.icon}
                  <span className="hidden sm:inline">{module.title}</span>
                </button>
              ))}
            </div>
            
            {/* Active Module Detail */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="text-orange-500 mb-4">{modules[activeModule].icon}</div>
                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                  {modules[activeModule].title}
                </h3>
                <p className="text-lg text-slate-600 mb-8">
                  {modules[activeModule].description}
                </p>
                <ul className="space-y-4">
                  {modules[activeModule].features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="order-1 lg:order-2">
                <div className="bg-slate-900 rounded-2xl p-4 shadow-2xl overflow-hidden">
                  <img 
                    src={moduleImages[activeModule]}
                    alt={modules[activeModule].title}
                    className="w-full rounded-xl shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('restaurantOS.whyChoose.title', 'Why Restaurants Choose Hambre OS')}
              </h2>
              <p className="text-lg text-slate-300">
                {t('restaurantOS.whyChoose.subtitle', "We've removed every barrier to getting started. No expensive hardware, no complex setup, no long-term contracts.")}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div 
                  key={index}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-orange-500/50 transition-colors"
                >
                  <div className="w-14 h-14 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-slate-400">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Social Proof / Testimonials */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('restaurantOS.testimonials.title', 'Trusted by Restaurants Everywhere')}
              </h2>
              <p className="text-lg text-slate-600">
                {t('restaurantOS.testimonials.subtitle', 'Join hundreds of restaurants already running on Hambre OS.')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index}
                  className="bg-slate-50 rounded-2xl p-8 border border-slate-200"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 text-lg mb-6 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-slate-50" id="pricing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('pricing.title', 'Simple, Transparent Pricing')}
              </h2>
              <p className="text-lg text-slate-600">
                {t('pricing.subtitle', 'No hidden fees. No long-term contracts. Cancel anytime.')}
              </p>
            </div>

            {/* Special Offer Banner */}
            <div className="max-w-3xl mx-auto mb-12">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-white text-center shadow-lg">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-3xl">🎉</span>
                  <h3 className="text-2xl font-bold">{t('specialOffer.pricingTitle', 'EARLY BIRD SPECIAL: 50% OFF')}</h3>
                  <span className="text-3xl">🎉</span>
                </div>
                <p className="text-lg text-green-100">
                  {t('specialOffer.pricingDescription', 'Sign up before April 1st and get 50% off for your first 12 months!')}
                </p>
                <p className="text-sm text-green-200 mt-2">
                  {t('specialOffer.pricingNote', 'Limited time offer. Prices shown reflect the discounted rate.')}
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {pricing.map((plan, index) => (
                <div 
                  key={index}
                  className={`relative rounded-2xl p-8 ${
                    plan.popular 
                      ? 'bg-slate-900 text-white shadow-xl shadow-slate-900/20 scale-105 lg:scale-110' 
                      : 'bg-white border border-slate-200'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                        {t('pricing.mostPopular', 'MOST POPULAR')}
                      </span>
                    </div>
                  )}
                  
                  <h3 className={`text-xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm mb-4 ${plan.popular ? 'text-slate-300' : 'text-slate-500'}`}>
                    {plan.description}
                  </p>
                  
                  <div className="mb-6">
                    {plan.price === t('pricing.custom', 'Custom') ? (
                      <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                        {t('pricing.custom', 'Custom')}
                      </span>
                    ) : (
                      <div>
                        <span className={`text-sm line-through ${plan.popular ? 'text-slate-400' : 'text-slate-400'}`}>
                          {currencySymbol}{getPrice(parseInt(plan.price))}
                        </span>
                        <span className={`text-4xl font-bold ml-2 ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                          {currencySymbol}{Math.round(getPrice(parseInt(plan.price)) * 0.5)}
                        </span>
                        <span className={plan.popular ? 'text-slate-300' : 'text-slate-500'}>/{t('pricing.perMonth', 'month')}</span>
                        <div className="mt-1">
                          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                            50% OFF
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className={`w-5 h-5 ${plan.popular ? 'text-orange-400' : 'text-green-500'}`} />
                        <span className={plan.popular ? 'text-slate-300' : 'text-slate-600'}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full rounded-full py-6 font-semibold ${
                      plan.popular 
                        ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                    onClick={() => plan.price === 'Custom' ? navigate('/contact') : navigate('/register?role=restaurant_admin')}
                  >
                    {plan.cta}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* VeriFactu Compliance Section */}
        <section className="py-20 bg-white" id="verifactu">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 rounded-full px-4 py-2 mb-4">
                <Shield className="w-5 h-5" />
                <span className="font-medium">{t('verifactu.badge', 'Spain Fiscal Compliance')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('verifactu.title', 'VeriFactu Ready')}
              </h2>
              <p className="text-lg text-slate-600">
                {t('verifactu.subtitle', 'Hambre is fully prepared for Spain\'s new anti-fraud invoicing requirements. Your business stays compliant automatically.')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left: What is VeriFactu */}
              <div className="space-y-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-red-800 mb-3 flex items-center gap-2">
                    <Bell className="w-6 h-6" />
                    {t('verifactu.mandatory.title', 'VeriFactu is Mandatory')}
                  </h3>
                  <p className="text-red-700 mb-4">
                    {t('verifactu.mandatory.description', 'Spain\'s anti-fraud regulation requires ALL businesses using invoicing software to comply with VeriFactu. This includes:')}
                  </p>
                  <ul className="space-y-2 text-red-700">
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-red-600" />
                      {t('verifactu.mandatory.companies', 'Companies (SL, SA)')}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-red-600" />
                      {t('verifactu.mandatory.freelancers', 'Freelancers (Autonomos)')}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-red-600" />
                      {t('verifactu.mandatory.restaurants', 'Restaurants & Bars')}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-red-600" />
                      {t('verifactu.mandatory.retail', 'Retail & All Businesses')}
                    </li>
                  </ul>
                </div>

                <div className="bg-slate-100 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">
                    {t('verifactu.requirements.title', 'What VeriFactu Requires')}
                  </h3>
                  <ul className="space-y-3 text-slate-700">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-orange-600 font-bold text-sm">1</span>
                      </div>
                      <span>{t('verifactu.requirements.integrity', 'Invoice integrity - No deletion or manipulation')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-orange-600 font-bold text-sm">2</span>
                      </div>
                      <span>{t('verifactu.requirements.traceability', 'Traceability - Complete audit trail')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-orange-600 font-bold text-sm">3</span>
                      </div>
                      <span>{t('verifactu.requirements.qr', 'QR codes on all invoices for verification')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-orange-600 font-bold text-sm">4</span>
                      </div>
                      <span>{t('verifactu.requirements.aeat', 'Automatic reporting to AEAT (Tax Authority)')}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right: Hambre is Ready */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-800">
                      {t('verifactu.ready.title', 'Hambre is Ready')}
                    </h3>
                    <p className="text-green-600">
                      {t('verifactu.ready.subtitle', '100% VeriFactu Compliant')}
                    </p>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="font-medium text-slate-900">{t('verifactu.features.hashChain', 'Cryptographic Hash Chain')}</span>
                      <p className="text-sm text-slate-600">{t('verifactu.features.hashChainDesc', 'Every invoice is cryptographically linked - impossible to tamper')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="font-medium text-slate-900">{t('verifactu.features.qrCodes', 'Automatic QR Codes')}</span>
                      <p className="text-sm text-slate-600">{t('verifactu.features.qrCodesDesc', 'Every receipt includes a verifiable QR code')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="font-medium text-slate-900">{t('verifactu.features.aeatIntegration', 'AEAT Integration')}</span>
                      <p className="text-sm text-slate-600">{t('verifactu.features.aeatIntegrationDesc', 'Automatic submission to Spanish Tax Authority')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="font-medium text-slate-900">{t('verifactu.features.ivaSupport', 'Spanish IVA Support')}</span>
                      <p className="text-sm text-slate-600">{t('verifactu.features.ivaSupportDesc', 'All Spanish tax rates: 21%, 10%, 4%, 0%')}</p>
                    </div>
                  </li>
                </ul>

                <Button 
                  onClick={() => navigate('/verifactu')}
                  className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full py-4 font-semibold"
                  data-testid="learn-verifactu"
                >
                  {t('verifactu.cta', 'Learn More About VeriFactu')} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('restaurantOS.cta.title', 'Ready to Simplify Your Restaurant Operations?')}
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              {t('restaurantOS.cta.subtitle', 'Join hundreds of restaurants already saving time and money with Hambre OS.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/register?role=restaurant_admin')}
                className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg font-semibold shadow-lg"
                data-testid="final-cta-start"
              >
                {t('restaurantOS.cta.trial', 'Start Your Free Trial')} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/contact')}
                className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
              >
                {t('restaurantOS.cta.sales', 'Talk to Sales')}
              </Button>
            </div>
            <p className="text-orange-200 mt-6 text-sm">
              {t('restaurantOS.cta.noCreditCard', '14-day free trial. No credit card required.')}
            </p>
          </div>
        </section>

        {/* Customer Ordering Section - Link to existing delivery app */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-orange-500 font-medium text-sm uppercase tracking-wider">{t('forCustomers.badge', 'For Customers')}</span>
                <h2 className="text-3xl font-bold text-white mt-2 mb-4">
                  {t('forCustomers.title')}
                </h2>
                <p className="text-slate-400 mb-6">
                  {t('forCustomers.description')}
                </p>
                <Link 
                  to="/delivery"
                  className="inline-flex items-center gap-2 text-orange-500 font-semibold hover:text-orange-400 transition-colors"
                  data-testid="link-to-delivery"
                >
                  {t('forCustomers.cta')} <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <UtensilsCrossed className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{t('forCustomers.stats.restaurants', '500+ Restaurants')}</p>
                      <p className="text-slate-400 text-sm">{t('forCustomers.stats.deliveryTakeaway', 'Delivery & Takeaway')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{t('forCustomers.stats.avgTime', '30 Min Average')}</p>
                      <p className="text-slate-400 text-sm">{t('forCustomers.stats.deliveryTime', 'Delivery Time')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
